import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, AlertCircle, ArrowLeft, RefreshCw, Copy, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { readAuthEvents, clearAuthEvents, type AuthBreadcrumb } from "@/contexts/AuthContext";
import { isNativeApp } from "@/lib/platform";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { StitchPage, StitchPanel, StitchHeading } from "@/components/stitch";
import { SEO } from "@/components/SEO";
import { MobileShell } from "@/components/mobile";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

type CheckStatus = "pass" | "fail" | "warn" | "pending";

interface Check {
  label: string;
  status: CheckStatus;
  detail: string;
}

const StatusIcon = ({ status }: { status: CheckStatus }) => {
  if (status === "pass") return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
  if (status === "fail") return <XCircle className="h-5 w-5 text-destructive" />;
  if (status === "warn") return <AlertCircle className="h-5 w-5 text-amber-500" />;
  return <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />;
};

type CallbackKind = "implicit" | "pkce" | "deep-link" | "none";

function classifyCallback(events: AuthBreadcrumb[]): CallbackKind {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (e.type === "callback-implicit") return "implicit";
    if (e.type === "callback-pkce") return "pkce";
    if (e.type === "deep-link-received") return "deep-link";
  }
  return "none";
}

function formatRoundTrip(events: AuthBreadcrumb[]): string | null {
  const initiated = [...events].reverse().find((e) => e.type === "oauth-initiated");
  const success = [...events].reverse().find((e) => e.type === "set-session-success");
  if (!initiated || !success) return null;
  const ms = success.at - initiated.at;
  if (ms < 0) return null;
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

/**
 * Diagnostics screen for verifying that the OAuth flow round-trips correctly
 * on the installed APK. Reads breadcrumbs persisted to sessionStorage by
 * AuthContext + OAuthCatchAll across the Chrome Custom Tab bounce.
 */
const AuthDiagnostics = () => {
  const { session, user, loading } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const [native, setNative] = useState(false);
  const [listenerInstalled, setListenerInstalled] = useState<boolean | null>(null);
  const [lastDeepLink, setLastDeepLink] = useState<string | null>(null);
  const [deepLinkAt, setDeepLinkAt] = useState<string | null>(null);
  const [events, setEvents] = useState<AuthBreadcrumb[]>([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setNative(isNativeApp());
  }, []);

  // Refresh breadcrumbs every second so newly-arrived callbacks show up.
  useEffect(() => {
    setEvents(readAuthEvents());
    const id = window.setInterval(() => setEvents(readAuthEvents()), 1000);
    return () => window.clearInterval(id);
  }, [tick]);

  const lastCallbackUrl = useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      return window.sessionStorage.getItem("fitaura.auth.lastCallbackUrl");
    } catch {
      return null;
    }
  }, [tick, events.length]);

  const callbackKind = useMemo(() => classifyCallback(events), [events]);
  const roundTrip = useMemo(() => formatRoundTrip(events), [events]);

  // Install our own listener (in addition to AuthContext's) so we can show the
  // raw URL the OS handed back. This is read-only — AuthContext still owns the
  // setSession() call.
  useEffect(() => {
    if (!isNativeApp()) {
      setListenerInstalled(false);
      return;
    }

    let handle: { remove: () => Promise<void> } | undefined;

    (async () => {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        handle = await CapApp.addListener("appUrlOpen", ({ url }) => {
          if (!url) return;
          setLastDeepLink(url);
          setDeepLinkAt(new Date().toLocaleTimeString());
        });
        setListenerInstalled(true);
      } catch (e) {
        console.warn("[diagnostics] could not install deep-link listener", e);
        setListenerInstalled(false);
      }
    })();

    return () => {
      handle?.remove().catch(() => undefined);
    };
  }, []);

  const checks: Check[] = [
    {
      label: "Running as native app",
      status: native ? "pass" : "warn",
      detail: native
        ? "Capacitor detected — native runtime active."
        : "You're in a web browser. Deep-link checks below only apply when this screen is opened from the installed APK.",
    },
    {
      label: "Deep-link listener installed",
      status: listenerInstalled === null ? "pending" : listenerInstalled ? "pass" : native ? "fail" : "warn",
      detail: listenerInstalled
        ? "@capacitor/app appUrlOpen listener is active. Backup path for fitauraai:// scheme."
        : native
        ? "Listener failed to install. The @capacitor/app plugin may be missing — run `npx cap sync android` and rebuild."
        : "Skipped — only relevant in the native APK.",
    },
    {
      label: "OAuth callback received",
      status:
        callbackKind !== "none"
          ? "pass"
          : lastDeepLink
          ? "warn"
          : "pending",
      detail:
        callbackKind !== "none"
          ? `Type: ${callbackKind}${lastCallbackUrl ? ` — ${lastCallbackUrl.slice(0, 80)}${lastCallbackUrl.length > 80 ? "…" : ""}` : ""}`
          : lastDeepLink
          ? `${deepLinkAt} — ${lastDeepLink.slice(0, 80)}${lastDeepLink.length > 80 ? "…" : ""}`
          : native
          ? "No callback yet. Trigger Google sign-in to test."
          : "Open this screen after signing in with Google to see the callback.",
    },
    {
      label: "Callback type",
      status: callbackKind === "none" ? "pending" : "pass",
      detail:
        callbackKind === "implicit"
          ? "Implicit flow (#access_token) — tokens parsed from URL hash."
          : callbackKind === "pkce"
          ? "PKCE flow (?code=…) — exchanged via exchangeCodeForSession()."
          : callbackKind === "deep-link"
          ? "Deep link (fitauraai://) — handled by AuthContext."
          : "No callback recorded yet.",
    },
    {
      label: "Round-trip time",
      status: roundTrip ? "pass" : "pending",
      detail: roundTrip
        ? `Sign-in tap → session active in ${roundTrip}.`
        : "Will populate after a complete sign-in cycle.",
    },
    {
      label: "Supabase session active",
      status: loading ? "pending" : session ? "pass" : "fail",
      detail: loading
        ? "Checking…"
        : session
        ? `Signed in as ${user?.email ?? user?.id ?? "unknown"}.`
        : "No active session. After Google login completes, this should flip to pass within ~1 second.",
    },
  ];

  const allPass = checks.every((c) => c.status === "pass");
  const anyFail = checks.some((c) => c.status === "fail");

  const copyDiagnostics = async () => {
    const payload = [
      `FitAura AI — Auth Diagnostics @ ${new Date().toISOString()}`,
      `Native: ${native}`,
      `Listener installed: ${listenerInstalled}`,
      `Callback kind: ${callbackKind}`,
      `Last callback URL: ${lastCallbackUrl ?? "(none)"}`,
      `Last deep link: ${lastDeepLink ?? "(none)"}`,
      `Deep link time: ${deepLinkAt ?? "(none)"}`,
      `Round-trip: ${roundTrip ?? "(incomplete)"}`,
      `Session: ${session ? "yes" : "no"}`,
      `User: ${user?.email ?? user?.id ?? "(none)"}`,
      `User-Agent: ${navigator.userAgent}`,
      `--- Breadcrumbs ---`,
      ...events.map(
        (e) =>
          `${new Date(e.at).toISOString()}  ${e.type}${e.detail ? `  ${e.detail}` : ""}`,
      ),
    ].join("\n");

    try {
      await navigator.clipboard.writeText(payload);
      toast({ title: "Diagnostics copied", description: "Paste into your bug report." });
    } catch {
      toast({
        title: "Copy failed",
        description: "Long-press the values to copy manually.",
        variant: "destructive",
      });
    }
  };

  const handleClearBreadcrumbs = () => {
    clearAuthEvents();
    try {
      window.sessionStorage.removeItem("fitaura.auth.lastCallbackUrl");
      window.sessionStorage.removeItem("fitaura.auth.lastCallbackSanitized");
    } catch {
      /* noop */
    }
    setTick((t) => t + 1);
    toast({ title: "Breadcrumbs cleared" });
  };

  const body = (
    <div className="mx-auto w-full max-w-2xl space-y-6 py-6">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm">
            <Link to="/app/profile">
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back
            </Link>
          </Button>
        </div>

        <StitchHeading
          eyebrow="Diagnostics"
          title="OAuth self-check"
          subtitle="Confirms the Google sign-in round-trip and that your Supabase session is live."
        />

        <StitchPanel className="space-y-4">
          <div
            className={cn(
              "rounded-lg border p-4 text-sm",
              allPass
                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                : anyFail
                ? "border-destructive/30 bg-destructive/10 text-destructive"
                : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
            )}
          >
            {allPass
              ? "✅ All checks passing — Google login is wired up correctly."
              : anyFail
              ? "❌ Something's wrong. See the failed check below."
              : "⏳ Some checks are pending. Trigger Google sign-in to complete the flow."}
          </div>

          <ul className="space-y-3">
            {checks.map((check) => (
              <li
                key={check.label}
                className="flex items-start gap-3 rounded-lg border border-border/60 bg-card/40 p-3"
              >
                <StatusIcon status={check.status} />
                <div className="flex-1 space-y-1">
                  <div className="text-sm font-medium">{check.label}</div>
                  <div className="text-xs text-muted-foreground break-words">{check.detail}</div>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={copyDiagnostics} variant="outline" size="sm">
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              Copy diagnostics
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/auth">Re-run Google sign-in</Link>
            </Button>
            <Button onClick={handleClearBreadcrumbs} variant="ghost" size="sm">
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Clear log
            </Button>
          </div>
        </StitchPanel>

        {events.length > 0 && (
          <StitchPanel className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-foreground">
              Event breadcrumbs ({events.length})
            </p>
            <ul className="space-y-1 text-[11px] font-mono text-muted-foreground">
              {events
                .slice()
                .reverse()
                .map((e, i) => (
                  <li key={`${e.at}-${i}`} className="break-all">
                    <span className="text-foreground/80">
                      {new Date(e.at).toLocaleTimeString()}
                    </span>{" "}
                    {e.type}
                    {e.detail ? <span className="text-foreground/60"> · {e.detail}</span> : null}
                  </li>
                ))}
            </ul>
          </StitchPanel>
        )}

        <StitchPanel className="space-y-2 text-xs text-muted-foreground">
          <p className="font-medium text-foreground">How to use this screen</p>
          <ol className="list-decimal space-y-1 pl-4">
            <li>Install the latest APK (or open the published web app).</li>
            <li>Tap "Re-run Google sign-in" and complete Google login in the Chrome tab.</li>
            <li>You'll be returned here. Callback type, round-trip, and session should all pass.</li>
            <li>If anything fails, tap "Copy diagnostics" and send to support.</li>
          </ol>
        </StitchPanel>
      </div>
  );

  return (
    <StitchPage>
      <SEO title="Auth Diagnostics — FitAura AI" description="Verify Google login round-trip flow." />
      {isMobile ? <MobileShell title="Diagnostics">{body}</MobileShell> : body}
    </StitchPage>
  );
};

export default AuthDiagnostics;
